import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, X, CalendarDays } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { DeliveryMenu } from '@/components/DeliveryMenu';
import { TID } from '@/lib/testIds';
import { PUBLIC_MENU_ENABLED } from '@/lib/launchMode';

const NAV = [
  { to: '/', label: 'Home' },
  ...(PUBLIC_MENU_ENABLED ? [{ to: '/menu', label: 'Menu' }] : []),
];

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-40 backdrop-blur bg-[hsl(var(--sb-bg))]/85 border-b border-[hsl(var(--sb-border))]">
      <div className="sb-flag-stripes" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid={TID.nav.headerLogo} className="flex items-center">
          <Logo variant="mobile" className="sm:hidden" />
          <Logo variant="header" className="hidden sm:inline-flex" taglineOverride={false} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/'}
              className={({ isActive }) => `px-3 py-2 text-sm rounded-md transition-colors ${isActive ? 'text-[hsl(var(--sb-gold))]' : 'text-white/80 hover:text-white hover:bg-white/5'}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Desktop: full-text Order for Delivery CTA (white fill, black text) */}
          <div className="hidden sm:inline-flex">
            <DeliveryMenu variant="desktop" />
          </div>
          {/*
            Mobile header intentionally omits a compact delivery icon here: the Hero
            surface hosts a prominent full-width "Order for Delivery" CTA above the
            eyebrow instead, so duplicating a small icon here would be redundant.
          */}
          <Link
            to="/book"
            data-testid="header-book-cta"
            className="inline-flex items-center gap-2 h-10 px-3 sm:px-4 rounded-full bg-[hsl(var(--sb-gold))] text-black font-semibold hover:bg-[hsl(var(--sb-gold-2))] transition-colors"
          >
            <CalendarDays size={16} />
            <span className="hidden sm:inline">Book a Table</span>
            <span className="sm:hidden">Book</span>
          </Link>
          <button
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-md border border-[hsl(var(--sb-border))] text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
          >
            {open ? <X size={18} /> : <MenuIcon size={18} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[hsl(var(--sb-border))] bg-[hsl(var(--sb-surface))]">
          <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                className={({ isActive }) => `py-3 text-sm border-b border-[hsl(var(--sb-border))] last:border-b-0 ${isActive ? 'text-[hsl(var(--sb-gold))]' : 'text-white/85'}`}
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
